import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { logger } from 'utils/logger';

// Create a service-specific logger
const log = logger.getLogger('AccountsService');

const baseUrl = environment.services.account.baseUrl;
const accountsUrl = baseUrl + environment.services.account.basePath;

export interface Account {
  id: number;
  name: string;
  lineOfBusiness: string;
  active?: boolean;
  lastUpdated: Date;
  // Additional fields for the enhanced account form
  listenPortalEnabled?: boolean;
  listenReportingOnly?: boolean; // Optional field for listen reporting
  strategyPortalEnabled?: boolean;
  selectiveAccess?: boolean;
  restrictionAccess?: any[];
  sfdcAccountId?: string; // SFDC Account Id field
  supersetRole?: string; // Superset Role field
  ssoEnabled?: boolean; // SSO Enabled field
  persistent?: boolean; // Persistent field - exempts account from automatic inactivity deactivation
}

export interface AuditedUser {
  id: number;
  username: string;
  robotic: boolean;
  enabled: boolean;
}

export interface AuditedAccount {
  id: number;
  name: string;
  effectiveLastActivity: number | string | null;
  users: AuditedUser[];
}

export interface InactivityRunSummary {
  auditOnly: boolean;
  accountsChecked: number;
  accountsDeactivated: number;
  usersDisabled: number;
  accounts: AuditedAccount[];
}

export interface AccountsFilter {
  lineOfBusiness?: string[];
  enabled?: 'all' | 'enabled' | 'disabled';
  accountName?: string;
  active?: boolean;
  updatedAfterDate?: string;
}

export interface AccountsParams {
  page: number;
  size: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  filters?: AccountsFilter;
}

class AccountsService {
  async getAccounts(params: AccountsParams): Promise<{data: Account[], count: number}> {
    try {
      let apiUrl = `${accountsUrl}/accounts/?page=${params.page}&size=${params.size}`;
      
      if (params.sort) {
        const sortFields = params.sort.split(',');
        sortFields.forEach(field => {
          apiUrl += `&sort=${field.trim()}`;
        });
        if (params.direction) {
          apiUrl += `&direction=${params.direction}`;
        }
      }

      if (params.filters) {
        if (params.filters.lineOfBusiness && params.filters.lineOfBusiness.length > 0) {
          params.filters.lineOfBusiness.forEach(lob => {
            apiUrl += `&lineOfBusiness=${encodeURIComponent(lob)}`;
          });
        }

        if (params.filters.enabled && params.filters.enabled !== 'all') {
          apiUrl += `&strategyPortalEnabled=${params.filters.enabled === 'enabled'}`;
        }

        if (params.filters.accountName && params.filters.accountName.trim() !== '') {
          apiUrl += `&search=${encodeURIComponent(params.filters.accountName.trim())}`;
        }

        if (params.filters.active === false) {
          apiUrl += `&active=false`;
        }

        if (params.filters.updatedAfterDate) {
          apiUrl += `&updatedAfterDate=${encodeURIComponent(params.filters.updatedAfterDate)}`;
        }
      }
      
      log.log('Calling API with URL:', apiUrl);
      
      const response = await apiService.makeRequest('get', apiUrl);
      const responseData = response.data;
      
      let total = 0;
      if (responseData && typeof responseData === 'object') {
        total = responseData.count || responseData.totalCount || responseData.total || 0;
      }
      
      let accountsArray = [];
      
      if (Array.isArray(responseData)) {
        accountsArray = responseData;
        total = response?.count || responseData.length;
      } else if (responseData && typeof responseData === 'object') {
        accountsArray = responseData.data || responseData.accounts || responseData.results || responseData.items || responseData.content || [];
        
        if (responseData.accounts && Array.isArray(responseData.accounts.items)) {
          accountsArray = responseData.accounts.items;
        }
        
        if (!Array.isArray(accountsArray) && responseData.id) {
          accountsArray = [responseData];
        }
      }
      
      if (!Array.isArray(accountsArray)) {
        throw new Error('Unexpected API response format');
      }
      
      const accountsData = accountsArray.map((item: any) => ({
        id: item.id,
        name: item.name,
        lineOfBusiness: item.lineOfBusiness || 'N/A',
        enabled: item.strategyPortalEnabled || false,
        active: item.active !== undefined ? item.active : true,
        listenPortalEnabled: item.listenPortalEnabled || false,
        listenReportingOnly: item.listenReportingOnly || false,
        strategyPortalEnabled: item.strategyPortalEnabled || false,
        selectiveAccess: item.selectiveAccess || false,
        restrictionAccess: item.restrictionAccess || [],
        sfdcAccountId: item.sfdcAccountId || '',
        supersetRole: item.supersetRole || '',
        ssoEnabled: item.ssoEnabled || false,
        persistent: item.persistent || false,
        lastUpdated: new Date(item.updatedDate || item.updateDate || item.createdDate || item.createDate || Date.now())
      }));
      
      return {
        data: accountsData,
        count: total
      };
    } catch (error) {
      log.error('Error fetching accounts:', error);
      throw error;
    }
  }

  async getAccount(id: number): Promise<Account> {
    try {
      const response = await apiService.makeRequest('get', `${accountsUrl}/accounts/${id}`);
      const accountData = response;
      
      return {
        id: accountData.id,
        name: accountData.name,
        lineOfBusiness: accountData.lineOfBusiness || 'N/A',
        active: accountData.active !== undefined ? accountData.active : true,
        listenPortalEnabled: accountData.listenPortalEnabled || false,
        listenReportingOnly: accountData.listenReportingOnly || false,
        strategyPortalEnabled: accountData.strategyPortalEnabled || false,
        selectiveAccess: accountData.selectiveAccess || false,
        restrictionAccess: accountData.restrictionAccess || [],
        sfdcAccountId: accountData.sfdcAccountId || '',
        supersetRole: accountData.supersetRole || '',
        ssoEnabled: accountData.ssoEnabled || false,
        persistent: accountData.persistent || false,
        lastUpdated: new Date(accountData.updatedDate || accountData.updateDate || accountData.createdDate || accountData.createDate || Date.now())
      };
    } catch (error) {
      log.error(`Error fetching account with ID ${id}:`, error);
      throw error;
    }
  }

  async updateAccount(account: Account): Promise<Account> {
    try {
      const response = await apiService.makeRequest('put', `${accountsUrl}/accounts/${account.id}`, account);

      // Invalidate cache for accounts list and this specific account
      apiService.invalidateCacheByPattern(`/accounts/?`);  // Clear accounts list cache
      apiService.invalidateCacheByPattern(`/accounts/${account.id}`); // Clear this account's cache

      return response;
    } catch (error) {
      log.error(`Error updating account with ID ${account.id}:`, error);
      throw error;
    }
  }

  async createAccount(account: Omit<Account, 'id'>): Promise<Account> {
    try {
      const response = await apiService.makeRequest('post', `${accountsUrl}/accounts`, account);

      // Invalidate cache for accounts list
      apiService.invalidateCacheByPattern(`/accounts/?`);

      return response;
    } catch (error) {
      log.error('Error creating account:', error);
      throw error;
    }
  }

  async changeAccountStatus(id: number, active: boolean): Promise<void> {
    try {
      await apiService.makeRequest('put', `${accountsUrl}/accounts/${id}/status`, { active }, false);
      apiService.invalidateCacheByPattern(`/accounts/${id}`);
      apiService.invalidateCacheByPattern(`/accounts/?`);
    } catch (error) {
      log.error(`Error changing status for account ${id}:`, error);
      throw error;
    }
  }

  async previewDeactivation(onlyPersistent: boolean = false): Promise<InactivityRunSummary> {
    try {
      const url = `${accountsUrl}/admin/inactivity/run?audit=true${onlyPersistent ? '&onlyPersistent=true' : ''}`;
      const response = await apiService.makeRequest('post', url, null, false);
      return response;
    } catch (error) {
      log.error('Error previewing deactivation:', error);
      throw error;
    }
  }

  async runDeactivation(): Promise<InactivityRunSummary> {
    try {
      const response = await apiService.makeRequest('post', `${accountsUrl}/admin/inactivity/run`, null, false);
      return response;
    } catch (error) {
      log.error('Error running deactivation:', error);
      throw error;
    }
  }
}

export const accountsService = new AccountsService();
export default accountsService;